// Navigation-aware obal univerzálního ListPage.
//
// ListPage engine (`@/shared/ui/list-page`) je záměrně navigation-agnostický (shared/ui nesmí
// importovat navigaci — vznikl by cyklus). Napojení aktivního pohledu na URL proto žije zde:
// aktivní záložku držíme v URL state paramu `view` (viz `listPageViewState`).
//   - reload / deeplink (view v URL) → obnoví se uložený pohled,
//   - čerstvé otevření z menu/navigátoru (view chybí) → první záložka („Vše").
// List-agenda musí deklarovat `state: listPageViewState`, aby se `view` serializoval do URL.

import { useCallback } from 'react';

import { ALL_VIEW_ID, ListPage, type ListPageConfig } from '@/shared/ui/list-page';

import type { AgendaHandle } from '../core/agendaDefinition';
import { useAgendaParams } from './useAgendaParams';
import { useAgendaState } from './useAgendaState';

interface ViewState {
  view?: string;
}

export interface NavListPageProps<TItem, TStats = unknown> {
  /** Agenda seznamu — musí mít `state: listPageViewState` (parametr `view`). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generická agenda handle
  agenda: AgendaHandle<any, any>;
  /** layerId aktuální vrstvy (předává `NavigationLayerHost`). */
  layerId: string;
  config: ListPageConfig<TItem, TStats>;
}

export function NavListPage<TItem, TStats = unknown>({
  agenda,
  layerId,
  config,
}: NavListPageProps<TItem, TStats>) {
  const { state } = useAgendaParams(agenda, layerId);
  const { patch, remove } = useAgendaState(agenda, layerId);
  const view = (state as ViewState).view;

  const onActiveViewChange = useCallback(
    (viewId: string) => {
      // „Vše" je výchozí — nepolir do URL (drž ji čistou); ostatní pohledy ulož.
      if (viewId === ALL_VIEW_ID) remove('view');
      else patch({ view: viewId });
    },
    [patch, remove]
  );

  return <ListPage config={config} initialViewId={view} onActiveViewChange={onActiveViewChange} />;
}
